import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MetaDataContext } from '../contexts/MetaDataContext'
import {
  MetaDataResponse,
  PowerRank,
  PowerPerformanceRiveStateType,
  PowerPerformanceForms
} from '../../types/mutual-fund-detail.types'
import { fetchInvestmentsData, fetchMutualFundData } from '../../repository/MutualFundDetail'
import { usePowerPerformanceRankQuery } from '../../hooks/usePowerPerformanceRankQuery'
import { onReady } from '../../utils/appBridge'
import { InvestmentDataType, TaxLiabilityDataResponseType } from '../../features/user-investment/types/investmentApi.types'
import { fetchTaxLiability } from '../../features/user-investment/repository/userInvestmentApi'

interface MetaDataProviderProps {
  children: ReactNode
  isin: string
  from: string
  disableOnReady?: boolean
}

const MetaDataProvider: React.FC<MetaDataProviderProps> = ({ children, isin, from, disableOnReady = false }) => {
  const [powerPerformanceRiveState, setPowerPerformanceRiveState] = useState<PowerPerformanceRiveStateType>(null)
  const [isPowerRiveReady, setIsPowerFormReady] = useState<boolean>(false)
  const [powerRank, setPowerRank] = useState<PowerRank>({ powerRank: 0, outOf: 0 })
  const [powerForm, setPowerForm] = useState<`${PowerPerformanceForms}` | null>(null)
  const [investmentData, setInvestmentData] = useState<InvestmentDataType>()
  const [taxLiabilityData, setTaxLiabilityData] = useState<TaxLiabilityDataResponseType>()
  const [isUserElite, setIsUserElite] = useState(false)
  const onReadyLock = useRef<boolean>(false)

  const handlePowerPerformanceRiveState = (powerPerformanceRiveStateParam: PowerPerformanceRiveStateType) =>
    setPowerPerformanceRiveState(powerPerformanceRiveStateParam)

  const handleIsPowerRiveReady = (isReady: boolean) => setIsPowerFormReady(isReady)
  const handleIsUserElite = (isElite: boolean) => setIsUserElite(isElite)
  const handlePowerRank = (powerRankParam: PowerRank) => setPowerRank(powerRankParam)
  const handlePowerForm = (powerFormParam: `${PowerPerformanceForms}`) => setPowerForm(powerFormParam)

  const {
    data: metadataFromQuery,
    isLoading: isMetaDataLoading,
    error: metaDataError,
    isSuccess: isMetaDataSuccess,
    isError: isMetaDataError
  } = useQuery<any, Error>({
    queryKey: ['mutualFundData', isin],
    queryFn: () => fetchMutualFundData(isin, from),
    enabled: !!isin,
    retry: 2,
    refetchOnWindowFocus: false
  })

  const {
    data: investmentDataFromQuery,
    isLoading: isInvestmentDataLoading,
    isSuccess: isInvestmentSuccess,
    isError: isInvestmentError
  } = useQuery<any, Error>({
    queryKey: ['fetchInvestmentData', isin],
    queryFn: () => fetchInvestmentsData(isin),
    enabled: !!isin,
    retry: 1,
    refetchOnWindowFocus: false
  })

  const { data: taxLiabilityDataFromQuery } = useQuery<any, Error>({
    queryKey: ['taxLiability', isin],
    queryFn: () => {
      return fetchTaxLiability(isin)
    },
    retry: 1,
    enabled: !!investmentData?.investedAmount?.amount,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (taxLiabilityDataFromQuery) {
      setTaxLiabilityData(() => taxLiabilityDataFromQuery?.data)
    }
  }, [taxLiabilityDataFromQuery])

  useEffect(() => {
    if (investmentDataFromQuery) {
      const dataCopy = investmentDataFromQuery?.data as InvestmentDataType
      setInvestmentData(() => dataCopy)
    }
  }, [investmentDataFromQuery])

  const handlePageReady = () => {
    if (!onReadyLock.current && !disableOnReady) {
      onReadyLock.current = true
      onReady()
    }
  }

  useEffect(() => {
    if ((isMetaDataSuccess || isMetaDataError) && isPowerRiveReady && (isInvestmentSuccess || isInvestmentError)) {
      handlePageReady()
    }
  }, [isPowerRiveReady, isMetaDataSuccess, isMetaDataError, isInvestmentSuccess, isInvestmentError])

  useEffect(() => {
    // Fallback for onReady
    setTimeout(() => {
      handlePageReady()
    }, 3000)
  }, [])

  const metadata: MetaDataResponse = useMemo(() => {
    return metadataFromQuery?.data
  }, [metadataFromQuery])

  const {
    powerPerformanceRankData,
    isPowerPerformanceRankLoading,
    isPowerPerformanceRankSuccess,
    isPowerPerformanceRankError
  } = usePowerPerformanceRankQuery({ isin, metadata })

  const isMutualFundDetailPageLoading =
    isMetaDataLoading || isInvestmentDataLoading || isPowerPerformanceRankLoading

  const { data: directFundData } = useQuery<any, Error>({
    queryKey: ['mutualFundData', metadata?.fundFamilyDirectFundIsin],
    queryFn: () => fetchMutualFundData(metadata?.fundFamilyDirectFundIsin, from),
    enabled: !!metadata?.fundFamilyDirectFundIsin,
    retry: 4
  })

  const directFundName = useMemo(() => {
    return (directFundData?.data as MetaDataResponse)?.fundDetails?.fundName ?? ''
  }, [directFundData])

  return (
    <MetaDataContext.Provider
      value={{
        data: metadata,
        loading: isMetaDataLoading,
        isMutualFundDetailPageLoading,
        isSuccess: isMetaDataSuccess,
        isError: isMetaDataError,
        error: metaDataError?.message,
        isin: isin,
        from: from,
        directFundName: directFundName,
        investmentData,
        taxLiabilityData,
        handleIsPowerRiveReady,
        isUserElite,
        handleIsUserElite,
        powerRank,
        handlePowerRank,
        powerForm,
        handlePowerForm,
        powerPerformanceRiveState,
        handlePowerPerformanceRiveState,
        powerPerformanceRankData,
        isPowerPerformanceRankLoading,
        isPowerPerformanceRankSuccess,
        isPowerPerformanceRankError
      }}
    >
      {children}
    </MetaDataContext.Provider>
  )
}

export default MetaDataProvider
